<template>
    <div>
        <div class="container-fluid">

            <!-- Header -->
            <div class="d-flex align-items-center justify-content-between pt-5 mb-4">
                <div class="d-block">
                    <div class="h1-lg display-tektur m-0">Import Nilai Rapor</div>
                    <div class="d-block text-sm text-muted mt-1">Nilai semester 1 sampai 5 untuk banyak siswa sekaligus.</div>
                </div>

                <div class="d-flex gap-2">
                    <button type="button" class="btn btn-outline-success" @click="openTemplateModal">
                        <i class="fa-solid fa-download fa-lg me-3"></i> Download Format
                    </button>
                    <button type="button" class="btn btn-outline-secondary" @click="$router.push({ name: 'rationalization' })">
                        <i class="fa-solid fa-arrow-left fa-lg me-2"></i> Kembali
                    </button>
                </div>
            </div>

            <!-- Upload Section -->
            <div class="card shadow-sm mb-4">
                <div class="card-body">
                    <div class="row align-items-end">
                        <div class="col-md-8">
                            <label class="form-label fw-bold">Upload File</label>
                            <input
                                ref="fileInput"
                                type="file"
                                class="form-control"
                                accept=".xlsx,.xls"
                                @change="handleFileUpload"
                            />
                        </div>

                        <div class="col-md-4 mt-md-0">
                            <button
                                class="btn btn-primary w-100"
                                :disabled="!file || fetch_process_file"
                                @click="processFile"
                            >
                                <span v-if="fetch_process_file" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {{ fetch_process_file ? 'Memproses...' : 'Proses File' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="fetch_process_file" class="d-flex justify-content-center my-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>

            <!-- Kesalahan yang tidak terkait satu siswa -->
            <div v-if="!fetch_process_file && fileErrors.length > 0" class="alert alert-danger mb-4">
                <div class="fw-bold mb-2">Beberapa baris tidak bisa dibaca:</div>
                <ul class="mb-0">
                    <li v-for="(item, index) in fileErrors" :key="index">{{ item }}</li>
                </ul>
            </div>

            <!-- Preview Table -->
            <div v-if="previewData.length > 0 && !fetch_process_file" class="card shadow-sm mb-4">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <div class="d-block">
                        <span class="fw-bold">Preview Data</span>
                        <span class="text-sm text-muted ms-2">{{ summary.total_valid }} dari {{ summary.total_student }} siswa siap diimpor</span>
                    </div>
                    <button class="btn btn-success btn-sm" @click="submitImport" :disabled="summary.total_valid < 1">
                        Import Sekarang
                    </button>
                </div>
                <div class="card-body">
                    <ul class="nav nav-tabs mb-3">
                        <li class="nav-item" v-for="semester in semesters" :key="semester">
                            <a class="nav-link" :class="previewSemester == semester ? 'active' : ''" href="javascript:void(0)" @click="previewSemester = semester">
                                Semester {{ semester }}
                                <span class="badge bg-secondary ms-1">{{ countSemester(semester) }}</span>
                            </a>
                        </li>
                    </ul>
                    <div class="table-responsive">
                        <table class="table table-bordered table-hover">
                            <thead class="table-light">
                                <tr>
                                    <th style="width:5%">No</th>
                                    <th>Nama</th>
                                    <th>Kelas</th>
                                    <th>Tahun Lulus</th>
                                    <th>Status</th>
                                    <th v-for="subject in previewSubjects" :key="subject.id" class="text-center" style="min-width:110px;">{{ subject.name }}</th>
                                    <th>Keterangan</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, index) in pagedPreview" :key="row.id">
                                    <td>{{ index + 1 + (previewPage - 1) * previewLimit }}</td>
                                    <td>
                                        <div class="d-block">{{ row.fullname || '-' }}</div>
                                        <div class="d-block small text-muted">ID {{ row.id }}</div>
                                    </td>
                                    <td>{{ row.class || '-' }}</td>
                                    <td>{{ row.graduation_year || '-' }}</td>
                                    <td>
                                        <span v-if="row.status === 'update'" class="badge bg-info text-dark">Ubah nilai</span>
                                        <span v-else-if="row.status === 'baru'" class="badge bg-warning text-dark">Rasionalisasi baru</span>
                                        <span v-else>-</span>
                                    </td>
                                    <td v-for="subject in previewSubjects" :key="subject.id" class="text-center">
                                        <template v-if="getCell(row, subject.id)">
                                            <span v-if="getCell(row, subject.id).invalid" class="text-danger fw-bold" title="Nilai tidak valid">{{ getCell(row, subject.id).value }}</span>
                                            <span v-else-if="getCell(row, subject.id).value === '-'" class="badge bg-secondary">Off</span>
                                            <span v-else class="fw-bold">{{ getCell(row, subject.id).value }}</span>
                                            <div v-if="getCell(row, subject.id).previous !== null && getCell(row, subject.id).previous !== undefined" class="small text-muted text-nowrap">
                                                sebelumnya {{ getCell(row, subject.id).previous === '-' ? 'Off' : getCell(row, subject.id).previous }}
                                            </div>
                                        </template>
                                    </td>
                                    <td>
                                        <ul v-if="row.errors.length > 0" class="mb-0 ps-3 text-danger small fw-bold">
                                            <li v-for="(error, indexError) in row.errors" :key="indexError">{{ error }}</li>
                                        </ul>
                                        <span v-else-if="row.total_change < 1" class="text-warning fw-bold">Tidak ada nilai diisi</span>
                                        <span v-else class="text-success">
                                            OK
                                            <span class="small text-muted">({{ row.total_score }} nilai<template v-if="row.total_off > 0">, {{ row.total_off }} Off</template>)</span>
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" v-if="previewTotalPages > 1">
                        <div class="small text-muted">Menampilkan {{ (previewPage - 1) * previewLimit + 1 }}-{{ Math.min(previewPage * previewLimit, previewData.length) }} dari {{ previewData.length }} siswa</div>
                        <v-pagination v-model="previewPage" :page-count="previewTotalPages" :classes="paginationStyle" :labels="paginationLabels"></v-pagination>
                    </div>
                    <div class="d-block small text-muted">Sel kosong = tidak diubah. <b>Off</b> = diisi "-" (mata pelajaran tidak diambil). Merah = nilai tidak valid. Angka di tab = jumlah siswa yang punya nilai di semester tersebut.</div>
                </div>
            </div>

            <!-- Ketentuan Import -->
            <div class="card border-warning mb-4">
                <div class="card-header bg-warning text-dark fw-bold">
                    Ketentuan Import
                </div>
                <div class="card-body">
                    <ul class="mb-0">
                        <li>Download format lewat tombol <b>Download Format</b>. Pilih mata pelajaran, tahun lulus (wajib) dan kelas (opsional). Siswa di format mengikuti filter tersebut.</li>
                        <li>Setiap semester ada di sheet terpisah: <code>Semester 1</code> sampai <code>Semester 5</code>.</li>
                        <li>Kolom No, ID_Siswa, Nama, Tahun Lulus dan Kelas terkunci dan tidak boleh diubah. Isi nilai hanya di kolom mata pelajaran.</li>
                        <li>Nilai berupa angka 0-100 (desimal boleh pakai titik atau koma). Isi <code>-</code> jika mata pelajaran tidak diambil di semester tersebut. Kosongkan jika tidak ingin diubah.</li>
                        <li>Jika siswa sudah punya rasionalisasi, nilai yang tersimpan sudah terisi di format. Import ulang akan mengubah nilainya.</li>
                        <li>Siswa yang belum punya rasionalisasi akan dibuatkan rasionalisasi baru berisi nilai saja. Lengkapi Biodata (kampus &amp; jurusan) di menu Rasionalisasi.</li>
                        <li>Siswa yang rasionalisasinya sudah berstatus diterima tidak bisa diubah.</li>
                        <li>Siswa dengan data yang salah akan dilewati seluruhnya. Perbaiki lalu import ulang.</li>
                        <li>Maksimal ukuran file 5MB dan 1000 siswa per sheet.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Modal Download Format -->
        <button type="button" class="d-none" id="btn-modal-score-template" data-bs-toggle="modal" data-bs-target="#score-template-modal">Klik</button>
        <div ref="templateModal" class="modal" id="score-template-modal" tabindex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="static">
            <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-body">
                        <div class="row align-items-center mb-4">
                            <div class="col">
                                <div class="h1 m-0 display-tektur">Download Format</div>
                            </div>
                            <div class="col-auto">
                                <button type="button" class="btn btn-white-naked btn-icon" data-bs-dismiss="modal" aria-label="Close" id="btn-close-modal-score-template">
                                    <i class="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                        </div>

                        <form v-on:submit.prevent="downloadTemplate">
                            <div class="d-block px-2">
                                <div class="row">
                                    <div class="col-md-6">
                                        <div class="form-group">
                                            <label class="form-label">Tahun Lulus<i class="fa-solid fa-circle-exclamation fa-sm ms-2 text-pink"></i></label>
                                            <div class="form-select-wrapper form-select-style-1">
                                                <i class="fa-solid fa-caret-down"></i>
                                                <select class="form-select form-select-box-1" v-model="template.graduation_year" @change="getClass">
                                                    <option value="">Pilih Tahun Lulus</option>
                                                    <option v-for="item in dataYear" :key="item.id" :value="item.id">{{ item.name }}</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group">
                                            <label class="form-label">Kelas <span class="text-muted small">(opsional)</span></label>
                                            <div class="form-select-wrapper form-select-style-1">
                                                <i class="fa-solid fa-caret-down"></i>
                                                <select class="form-select form-select-box-1" v-model="template.class" :disabled="!template.graduation_year || fetch_class">
                                                    <option value="">{{ fetch_class ? 'Memuat kelas...' : 'Semua Kelas' }}</option>
                                                    <option v-for="item in dataClass" :key="item" :value="item">{{ item }}</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group mb-0">
                                    <div class="d-flex align-items-center justify-content-between mb-2">
                                        <label class="form-label m-0">
                                            Mata Pelajaran<i class="fa-solid fa-circle-exclamation fa-sm ms-2 text-pink"></i>
                                            <span class="text-muted small ms-2">{{ template.subject_ids.length }} dipilih</span>
                                        </label>
                                        <div class="d-block">
                                            <button type="button" class="btn btn-white-naked btn-sm me-1" @click="template.subject_ids = dataSubject.map(item => item.id)">Pilih semua</button>
                                            <button type="button" class="btn btn-white-naked btn-sm" @click="template.subject_ids = []">Hapus pilihan</button>
                                        </div>
                                    </div>
                                    <div v-if="dataSubject.length < 1" class="text-sm text-muted">Memuat mata pelajaran...</div>
                                    <div class="row border rounded py-2 mx-0" style="max-height:300px; overflow-y:auto;">
                                        <div class="col-md-6" v-for="item in dataSubject" :key="item.id">
                                            <div class="form-check my-1">
                                                <input class="form-check-input" type="checkbox" :id="'subject-'+item.id" :value="item.id" v-model="template.subject_ids">
                                                <label class="form-check-label" :for="'subject-'+item.id">{{ item.name }}</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="d-block small text-muted mt-2">Setiap mata pelajaran yang dipilih jadi kolom nilai di setiap sheet semester.</div>
                                </div>
                            </div>

                            <div class="d-flex align-items-center justify-content-between border-top pt-4 mt-4">
                                <div class="d-block small">
                                    <i class="fa-solid fa-circle-exclamation me-2 text-pink"></i>Wajib diisi
                                </div>
                                <div class="d-flex align-items-center">
                                    <button type="button" class="btn btn-white-naked btn-s-1 me-3" data-bs-dismiss="modal" aria-label="Close">
                                        <div class="btn-s-1-inner">Batal</div>
                                    </button>
                                    <button type="submit" class="btn btn-primary btn-s-1" :disabled="fetch_template">
                                        <div class="btn-s-1-inner">{{ fetch_template ? 'Harap tunggu ...' : 'Download Format' }}</div>
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import vPagination from 'vue-plain-pagination'

export default {
    name: 'ScoreImport',
    components: {
        vPagination
    },
    metaInfo: {
        title: 'Import Nilai Rapor',
        titleTemplate: '%s - Rasionalisasi Rapor'
    },
    data() {
        return {
            semesters           : [1, 2, 3, 4, 5],
            dataYear            : [],
            dataClass           : [],
            dataSubject         : [],
            template            : {
                graduation_year : '',
                class           : '',
                subject_ids     : []
            },
            fetch_class         : false,
            fetch_template      : false,
            file                : null,
            previewData         : [],
            previewSubjects     : [],
            fileErrors          : [],
            summary             : {
                total_student   : 0,
                total_valid     : 0
            },
            fetch_process_file  : false,
            previewSemester     : 1,
            previewPage         : 1,
            previewLimit        : 10,
            importLimit         : 10,
            paginationStyle     : {
                ul: 'list-pagination pagination pagination-tabs card-pagination m-0',
                li: 'page-item',
                a: 'page',
                liActive: 'active',
                liDisable: 'disabled',
                button: 'page-link'
            },
            paginationLabels    : {
                first: '',
                prev: '',
                next: '',
                last: ''
            }
        }
    },
    computed: {
        previewTotalPages: function() {
            return Math.max(1, Math.ceil(this.previewData.length / this.previewLimit));
        },
        pagedPreview: function() {
            let start = (this.previewPage - 1) * this.previewLimit;
            return this.previewData.slice(start, start + this.previewLimit);
        }
    },
    created() {
        this.getYear();
        this.getSubject();
    },
    mounted() {
        // Modal harus anak langsung <body>; di dalam layout, backdrop Bootstrap menutupi modal.
        document.body.appendChild(this.$refs.templateModal);
    },
    beforeDestroy() {
        let modal = this.$refs.templateModal;

        if (modal && modal.classList.contains('show')) {
            document.getElementById('btn-close-modal-score-template').click();
        }

        // Bersihkan sisa backdrop kalau halaman ditinggal saat modal terbuka.
        document.querySelectorAll('.modal-backdrop').forEach(item => item.remove());
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');

        if (modal && modal.parentNode) {
            modal.parentNode.removeChild(modal);
        }
    },
    methods: {
        getYear: function() {
            this.$http.get(process.env.VUE_APP_APIHOST + 'year/list').then(res => {
                this.dataYear = res.data.data || [];
            });
        },
        getSubject: function() {
            this.$http.get(process.env.VUE_APP_APIHOST + 'subject/list', {
                params : {
                    flag  : 'general',
                    limit : 1000
                }
            }).then(res => {
                this.dataSubject = res.data.data || [];
            });
        },
        getClass: function() {
            this.template.class = '';
            this.dataClass      = [];

            if (!this.template.graduation_year) {
                return;
            }

            this.fetch_class = true;
            this.$http.get(process.env.VUE_APP_APIHOST + 'rationalization/import-score/classes', {
                params : {
                    graduation_year : this.template.graduation_year
                }
            }).then(res => {
                this.fetch_class = false;
                this.dataClass   = res.data.status ? res.data.data : [];
            }).catch(() => {
                this.fetch_class = false;
            });
        },
        openTemplateModal: function() {
            document.getElementById('btn-modal-score-template').click();
        },
        downloadTemplate: function() {
            if (!this.template.graduation_year) {
                this.$swal({ icon: 'warning', title: 'Ups!', text: 'Harap pilih tahun lulus.' });
                return;
            }

            if (this.template.subject_ids.length < 1) {
                this.$swal({ icon: 'warning', title: 'Ups!', text: 'Harap pilih minimal satu mata pelajaran.' });
                return;
            }

            // Kolom mengikuti urutan daftar mata pelajaran, bukan urutan klik.
            let subjectIds = this.dataSubject
                .map(item => item.id)
                .filter(id => this.template.subject_ids.includes(id));

            this.fetch_template = true;
            this.$http.get(process.env.VUE_APP_APIHOST + 'rationalization/import-score/template', {
                params : {
                    graduation_year : this.template.graduation_year,
                    class           : this.template.class,
                    subject_ids     : subjectIds.join(',')
                }
            }).then(res => {
                this.fetch_template = false;

                if (!res.data.status) {
                    this.$swal({ icon: 'warning', title: 'Ups!', text: res.data.message });
                    return;
                }

                this.saveBase64File(res.data.data.file, res.data.data.filename);
                document.getElementById('btn-close-modal-score-template').click();

                this.$swal({
                    icon: 'success',
                    title: 'Format berhasil di-download',
                    text: `Format berisi ${res.data.data.total_student} siswa. Isi nilai lalu upload kembali di halaman ini.`,
                });
            }).catch(() => {
                this.fetch_template = false;
                this.$swal({ icon: 'warning', title: 'Ups!', text: 'Please check your internet connection.' });
            });
        },
        saveBase64File: function(base64, filename) {
            let binary = atob(base64);
            let bytes  = new Uint8Array(binary.length);

            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }

            let blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            let url  = URL.createObjectURL(blob);
            let link = document.createElement('a');

            link.href     = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },
        getCell: function(row, subjectId) {
            let cells = row.scores ? row.scores[this.previewSemester] : null;
            return cells ? cells[subjectId] : null;
        },
        countSemester: function(semester) {
            return this.previewData.filter(row => row.scores && row.scores[semester] && Object.keys(row.scores[semester]).length > 0).length;
        },
        handleFileUpload: function(event) {
            this.file            = event.target.files[0] || null;
            this.previewData     = [];
            this.previewSubjects = [];
            this.fileErrors      = [];
        },
        buildFormData: function() {
            let formData = new FormData();
            formData.append('file', this.file);
            return formData;
        },
        processFile: function() {
            if (!this.file) {
                this.$swal({ icon: 'warning', title: 'Ups!', text: 'Harap pilih file terlebih dahulu.' });
                return;
            }

            this.fetch_process_file = true;
            this.previewData        = [];
            this.previewSubjects    = [];
            this.fileErrors         = [];

            this.$http.post(process.env.VUE_APP_APIHOST + 'rationalization/import-score/preview', this.buildFormData(), {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }).then(response => {
                this.fetch_process_file = false;

                if (!response.data.status) {
                    this.$swal({ icon: 'warning', title: 'Ups!', text: response.data.message });
                    return;
                }

                this.previewData     = response.data.data;
                this.previewSubjects = response.data.subjects;
                this.fileErrors      = response.data.errors;
                this.summary         = response.data.summary;
                this.previewSemester = 1;
                this.previewPage     = 1;
            }).catch(() => {
                this.fetch_process_file = false;
                this.$swal({ icon: 'warning', title: 'Ups!', text: 'Please check your internet connection.' });
            });
        },
        submitImport: function() {
            let totalValid  = this.summary.total_valid;
            let totalNew    = this.previewData.filter(row => row.status === 'baru' && row.errors.length < 1 && row.total_change > 0).length;
            let totalSkip   = this.summary.total_student - totalValid;
            let text        = `Nilai ${totalValid} siswa akan disimpan.`;

            if (totalNew > 0) {
                text += ` ${totalNew} di antaranya belum punya rasionalisasi dan akan dibuatkan baru.`;
            }

            if (totalSkip > 0) {
                text += ` ${totalSkip} siswa dilewati karena ada kesalahan atau tidak ada nilai.`;
            }

            this.$swal.fire({
                title: 'Konfirmasi Import',
                text: text + ' Lanjutkan?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Ya, impor sekarang!',
                cancelButtonText: 'Batal'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.runImport();
                }
            });
        },
        setImportProgress: function(processed, total) {
            let percent = total > 0 ? Math.round((processed / total) * 100) : 0;
            let bar     = document.getElementById('import-score-progress');
            let label   = document.getElementById('import-score-progress-text');

            if (bar) {
                bar.style.width = percent + '%';
                bar.textContent = percent + '%';
            }

            if (label) {
                label.textContent = `${processed} dari ${total} siswa diproses`;
            }
        },
        // Kirim file yang sama per batch (page/limit) supaya progres terlihat dan request tidak terlalu berat.
        runImport: async function() {
            let total   = this.summary.total_student;
            let page    = 1;
            let totals  = { update: 0, create: 0, skip: 0 };
            let failed  = [];

            this.$swal.fire({
                title: 'Sedang Mengimpor...',
                html: `<div class="progress" style="height:20px;"><div id="import-score-progress" class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style="width:0%;">0%</div></div>
                       <div id="import-score-progress-text" class="small mt-2">0 dari ${total} siswa diproses</div>`,
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false
            });

            try {
                let hasMore = true;

                while (hasMore) {
                    let formData = this.buildFormData();
                    formData.append('page', page);
                    formData.append('limit', this.importLimit);

                    let response = await this.$http.post(process.env.VUE_APP_APIHOST + 'rationalization/import-score/data', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    if (!response.data.status) {
                        throw new Error(response.data.message || 'Import gagal.');
                    }

                    let data = response.data.data;
                    totals.update += data.total_update;
                    totals.create += data.total_create;
                    totals.skip   += data.total_skip;
                    failed         = failed.concat(data.failed || []);

                    this.setImportProgress(data.processed, data.total_student);
                    hasMore = data.has_more;
                    page++;
                }
            } catch (error) {
                this.$swal.close();
                let saved = totals.update + totals.create;
                let note  = saved > 0 ? ` ${saved} siswa sudah tersimpan sebelum proses berhenti.` : '';

                this.$swal({
                    icon: 'error',
                    title: 'Import berhenti',
                    text: (error && error.message ? error.message : 'Please check your internet connection.') + note,
                });
                return;
            }

            this.$swal.close();
            let saved = totals.update + totals.create;

            if (saved < 1) {
                this.$swal({ icon: 'warning', title: 'Ups!', text: 'Tidak ada nilai yang berhasil diimpor. Periksa kembali data pada file.' });
                return;
            }

            let message = `${saved} siswa berhasil diimpor (${totals.update} diperbarui, ${totals.create} rasionalisasi baru).`;
            if (totals.skip > 0) {
                message += ` ${totals.skip} siswa dilewati.`;
            }
            if (failed.length > 0) {
                message += ` ${failed.length} siswa gagal disimpan: ${failed.join(', ')}.`;
            }

            this.$swal({
                icon: failed.length > 0 ? 'warning' : 'success',
                title: failed.length > 0 ? 'Import selesai dengan catatan' : 'Berhasil!',
                text: message,
            });

            this.file            = null;
            this.previewData     = [];
            this.previewSubjects = [];
            this.fileErrors      = [];
            this.$refs.fileInput.value = '';
        }
    }
}
</script>

<style scoped>
.card {
    border-radius: 12px;
}
</style>
